import { NAVIGATOR_QUERY_KEYS } from 'app/modules/navigator/queries/keys';

import { get } from 'app/shared/utils/fetchr';
import getFromProcessEnv from 'app/shared/utils/getFromProcessEnv';
import { selectOutdatedIndicatorEnabled } from 'app/shared/featureFlags/selectors';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

interface AppVersionResponse {
  version: string;
}

interface DeployedVersions {
  backendVersion: string;
  frontendVersion: string;
}

const FRONTEND_HEAD_HASH_PATTERN = /REACT_APP_HEAD_HASH:\s*"([^"]+)"/;
const FRONTEND_REFETCH_INTERVAL_MS = 5 * 60 * 1000;
const LOCAL_FRONTEND_REFETCH_INTERVAL_MS = 10 * 1000;

const getAppVersion = (): Promise<AppVersionResponse> => get('/v1/app-version');
const getRuntimeFrontendAppVersion = (): string =>
  getFromProcessEnv('REACT_APP_HEAD_HASH');

export const parseFrontendAppVersion = (envConfigText: string): string => {
  return envConfigText.match(FRONTEND_HEAD_HASH_PATTERN)?.[1] ?? '';
};

export const getAppVersionRefetchIntervalMs = (
  nodeEnv = process.env.NODE_ENV,
): number =>
  nodeEnv === 'development'
    ? LOCAL_FRONTEND_REFETCH_INTERVAL_MS
    : FRONTEND_REFETCH_INTERVAL_MS;

const getFrontendAppVersion = async (): Promise<string> => {
  const response = await fetch(`/env-config.js?ts=${Date.now()}`, {
    cache: 'no-store',
  });
  if (!response.ok) {
    throw new Error(`Failed to fetch env-config.js (${response.status})`);
  }

  return (
    parseFrontendAppVersion(await response.text()) ||
    getRuntimeFrontendAppVersion()
  );
};

const getDeployedVersions = async (): Promise<DeployedVersions> => {
  const [backendResponse, frontendVersion] = await Promise.allSettled([
    getAppVersion(),
    getFrontendAppVersion(),
  ]);

  if (backendResponse.status === 'rejected') {
    throw backendResponse.reason;
  }

  return {
    backendVersion: backendResponse.value.version,
    frontendVersion:
      frontendVersion.status === 'fulfilled'
        ? frontendVersion.value
        : getRuntimeFrontendAppVersion(),
  };
};

export const hasAppVersionChanged = (
  initialVersions: DeployedVersions,
  currentVersions: DeployedVersions,
): boolean => {
  return (
    initialVersions.backendVersion !== currentVersions.backendVersion ||
    initialVersions.frontendVersion !== currentVersions.frontendVersion
  );
};

export const useAppVersionChanged = () => {
  const [initialVersions, setInitialVersions] =
    useState<DeployedVersions | null>(null);
  const featureFlagEnabled = useSelector(selectOutdatedIndicatorEnabled);
  const enabled = featureFlagEnabled || process.env.NODE_ENV === 'development';

  const { data } = useQuery({
    enabled,
    meta: {
      errorMessage: 'Failed to fetch app versions',
    },
    queryFn: getDeployedVersions,
    queryKey: NAVIGATOR_QUERY_KEYS.getVersion(),
    refetchInterval: (query) => {
      const currentVersions = query.state.data;
      if (
        currentVersions &&
        initialVersions &&
        hasAppVersionChanged(initialVersions, currentVersions)
      ) {
        return false;
      }
      return getAppVersionRefetchIntervalMs();
    },
  });

  useEffect(() => {
    if (initialVersions || !data?.backendVersion) {
      return;
    }

    setInitialVersions({
      backendVersion: data.backendVersion,
      frontendVersion: getRuntimeFrontendAppVersion(),
    });
  }, [data, initialVersions]);

  return initialVersions && data
    ? hasAppVersionChanged(initialVersions, data)
    : false;
};
